/**
 * The sheet a creator picks the music from.
 *
 * One sheet for both studios, with the same chrome as the block picker: a drag
 * handle, a title, a close button, a backdrop that dismisses. Inside it: a search
 * field, the mood categories, and a row per track with its length, an audition
 * button and the choice itself. The audition is a *player* rather than a play
 * button: it can be held where it is and continued from there, and the row it is
 * in draws how far in it has got.
 *
 * It is the *only* place a creator meets the music, so it also has to be the
 * place the three states are legible, because two of them look identical on a
 * page (nothing plays either way):
 *
 *   · a track chosen      — that track's name is on the header line
 *   · silence chosen      — "No background music", and a way back
 *   · never chosen        — the theme's own bed plays, and the header says so
 *
 * Deliberately does not close when a track is chosen. Auditioning is a sequence —
 * hear one, hear the next, go back — and a sheet that shut on the first tap would
 * make choosing music more work than choosing a theme. The choice is applied the
 * moment it is made, the row marks itself, and the creator closes when they are
 * done.
 *
 * The audition is owned by this sheet and stopped when it goes away: leaving a
 * screen is not the same as pressing stop, and a track that follows a creator out
 * of the sheet is the bug `#/lib/musicPlayback` exists to make impossible.
 */
import {useEffect, useMemo, useState} from 'react'
import {ScrollView, StyleSheet, TouchableOpacity, View} from 'react-native'
import {Feather} from '@expo/vector-icons'
import {useLingui} from '@lingui/react/macro'

import {
  type BackgroundMusicChoice,
  browseMusic,
  countInCategory,
  findMusicTrack,
  formatTrackDuration,
  MUSIC_CATEGORIES,
  type MusicCategoryId,
  type MusicTrack,
} from '#/lib/backgroundMusic'
import {MUSIC_LIBRARY} from '#/lib/backgroundMusicLibrary'
import {
  musicPreview,
  pauseMusicPreview,
  previewMusicTrack,
  resumeMusicPreview,
  stopMusicPreview,
  subscribeMusicPreview,
} from '#/lib/musicPlayback'
import {StudioTextField} from '#/components/studio/StudioTextField'
import {Text} from '#/components/Typography'
import {type StudioChrome} from '#/themes-studio/chrome'
import {useStudioChrome, useStudioStyles} from '#/themes-studio/useChrome'

/**
 * How often the audition's clock is read while it plays.
 *
 * Four times a second: enough for a bar that moves smoothly, cheap enough that a
 * sheet of forty rows is not forty renders a second — only the row being
 * auditioned reads the time it draws from this.
 */
const PREVIEW_TICK_MS = 250

/** What the header line says about the current choice. */
export function backgroundMusicStatus(choice: BackgroundMusicChoice): string {
  if (choice === undefined) return 'Theme music'
  if (choice === null) return 'No music'
  return findMusicTrack(MUSIC_LIBRARY, choice)?.title ?? 'No music'
}

export function BackgroundMusicSheet({
  selected,
  onSelect,
  onClose,
  testID = 'studioMusicSheet',
}: {
  /** The creator's current choice, in the studio's own three states. */
  selected: BackgroundMusicChoice
  /** Applied immediately; `null` is deliberate silence. */
  onSelect: (choice: BackgroundMusicChoice) => void
  onClose: () => void
  testID?: string
}) {
  const c = useStudioChrome()
  const ss = useStyles()
  const {t: l} = useLingui()
  const [category, setCategory] = useState<MusicCategoryId>('all')
  const [query, setQuery] = useState('')
  const [preview, setPreview] = useState(() => musicPreview())

  const rows = useMemo(
    () => browseMusic(MUSIC_LIBRARY, {category, query}),
    [category, query],
  )

  // The audition outlives this component otherwise, and an audition is not a
  // setting: closing the sheet has to silence it.
  useEffect(() => {
    const unsubscribe = subscribeMusicPreview(() => setPreview(musicPreview()))
    return () => {
      unsubscribe()
      stopMusicPreview()
    }
  }, [])

  /*
   * A progress bar is a clock, and the clock is the platform's: this asks it
   * again while something is playing. Held is not polled — a reading cannot move
   * while it is held — and the interval goes with the sheet.
   */
  const auditionId = preview?.id ?? null
  const auditionHeld = preview?.paused ?? true
  useEffect(() => {
    if (!auditionId || auditionHeld) return
    const tick = setInterval(() => setPreview(musicPreview()), PREVIEW_TICK_MS)
    return () => clearInterval(tick)
  }, [auditionId, auditionHeld])

  const audition = (track: MusicTrack) => {
    if (preview?.id !== track.id) {
      void previewMusicTrack(track)
      return
    }
    // The row is already the audition, so the button is its *control*: a tap
    // holds it where it is, and the next continues from there rather than from
    // the beginning.
    if (preview.paused) resumeMusicPreview()
    else pauseMusicPreview()
  }

  return (
    <>
      <TouchableOpacity
        accessibilityRole="button"
        accessibilityLabel={l`Close the music browser`}
        accessibilityHint={l`Dismisses the music browser`}
        activeOpacity={1}
        onPress={onClose}
        style={ss.backdrop}
      />
      <View style={ss.sheet} testID={testID}>
        <View style={ss.handle} />
        <View style={ss.header}>
          <View style={ss.headerText}>
            <Text style={ss.title}>{l`Background music`}</Text>
            <Text
              style={ss.subtitle}
              numberOfLines={1}
              testID={`${testID}Status`}>
              {l`Plays under every page`}
              {' · '}
              {backgroundMusicStatus(selected)}
            </Text>
          </View>
          <TouchableOpacity
            accessibilityRole="button"
            accessibilityLabel={l`Close`}
            accessibilityHint={l`Keeps the current music`}
            onPress={onClose}
            testID={`${testID}Close`}
            style={ss.close}>
            <Feather name="x" size={16} color={c.text} />
          </TouchableOpacity>
        </View>

        <View style={ss.searchWrap}>
          <StudioTextField
            value={query}
            onChangeText={setQuery}
            placeholder={l`Search music`}
            accessibilityLabel={l`Search music`}
            accessibilityHint={l`Filters the tracks in this mood by name`}
            testID={`${testID}Search`}
            style={ss.search}
            returnKeyType="search"
          />
        </View>

        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={ss.chipsScroll}
          contentContainerStyle={ss.chips}>
          {MUSIC_CATEGORIES.map(chip => {
            const active = chip.id === category
            const count = countInCategory(MUSIC_LIBRARY, chip.id)
            return (
              <TouchableOpacity
                key={chip.id}
                accessibilityRole="button"
                accessibilityLabel={l(chip.label)}
                accessibilityHint={l`Filters the list to this mood`}
                onPress={() => setCategory(chip.id)}
                testID={`${testID}Category-${chip.id}`}
                style={[
                  ss.chip,
                  active && {
                    backgroundColor: c.accentTint,
                    borderColor: c.accentBorder,
                  },
                ]}>
                <Text style={[ss.chipTxt, active && {color: c.accent}]}>
                  {l(chip.label)}
                </Text>
                {chip.id !== 'all' && count > 0 && (
                  <Text style={[ss.chipCount, active && {color: c.accent}]}>
                    {count}
                  </Text>
                )}
              </TouchableOpacity>
            )
          })}
        </ScrollView>

        <ScrollView
          style={ss.list}
          contentContainerStyle={ss.listContent}
          keyboardShouldPersistTaps="handled">
          {rows.length === 0 ? (
            <View style={ss.empty} testID={`${testID}Empty`}>
              <Feather name="music" size={20} color={c.textFaint} />
              <Text style={ss.emptyTxt}>
                {query
                  ? l`No track matches “${query}”`
                  : l`Nothing in this mood yet`}
              </Text>
            </View>
          ) : (
            rows.map(track => {
              const chosen = selected === track.id
              const isAudition = preview?.id === track.id
              const isPlaying = isAudition && !preview?.paused
              // The length drawn is the platform's when it can say it, and the
              // catalogue's own until then: an element knows its duration only
              // once the file's header has arrived.
              const totalMs =
                (isAudition ? preview?.durationMs : null) ?? track.durationMs
              const elapsedMs = Math.min(
                (isAudition ? preview?.positionMs : null) ?? 0,
                totalMs,
              )
              const progressPct =
                totalMs > 0 ? Math.min(100, (elapsedMs / totalMs) * 100) : 0
              const mood = MUSIC_CATEGORIES.find(
                cat => cat.id === track.category,
              )
              return (
                /*
                 * A row is a *container*, not a button.
                 *
                 * The audition has to be pressable inside the row, and a button
                 * inside a button is not valid markup: the web build warned about
                 * it, and a nested press target is the kind of thing that silently
                 * swallows the wrong tap. So the row holds two siblings — the
                 * audition, and the choice — and only the choice selects.
                 */
                <View
                  key={track.id}
                  testID={`${testID}Row-${track.id}`}
                  style={[
                    ss.row,
                    chosen && {
                      borderColor: c.accentBorder,
                      backgroundColor: c.accentTint,
                    },
                  ]}>
                  <TouchableOpacity
                    accessibilityRole="button"
                    accessibilityLabel={
                      isPlaying
                        ? `Pause ${track.title}`
                        : isAudition
                          ? `Resume ${track.title}`
                          : `Preview ${track.title}`
                    }
                    accessibilityHint={l`Plays this track for you here`}
                    onPress={() => audition(track)}
                    testID={`${testID}Preview-${track.id}`}
                    style={[
                      ss.audition,
                      isAudition && {
                        backgroundColor: c.accentFill,
                        borderColor: c.accentFill,
                      },
                    ]}>
                    <Feather
                      name={isPlaying ? 'pause' : 'play'}
                      size={14}
                      color={isAudition ? c.onAccent : c.accent}
                      /* The triangle sits left of its own centre in the glyph,
                         which is the nudge every player makes: without it a
                         play reads as a circle with a bite taken out. */
                      style={isPlaying ? undefined : ss.playNudge}
                    />
                  </TouchableOpacity>

                  <TouchableOpacity
                    accessibilityRole="button"
                    accessibilityLabel={track.title}
                    accessibilityHint={
                      chosen
                        ? l`Already the background music`
                        : l`Uses this track under every page`
                    }
                    onPress={() => onSelect(track.id)}
                    style={ss.rowTap}>
                    <View style={ss.rowText}>
                      <Text style={ss.rowTitle} numberOfLines={1}>
                        {track.title}
                      </Text>
                      <Text
                        style={ss.rowMeta}
                        numberOfLines={1}
                        testID={`${testID}Time-${track.id}`}>
                        {isAudition
                          ? `${formatTrackDuration(elapsedMs)} / ${formatTrackDuration(totalMs)}`
                          : formatTrackDuration(totalMs)}
                        {mood ? ` · ${l(mood.label)}` : ''}
                      </Text>
                    </View>

                    <View
                      style={[
                        ss.mark,
                        chosen && {
                          backgroundColor: c.accentFill,
                          borderColor: c.accentFill,
                        },
                      ]}>
                      {chosen && (
                        <Feather name="check" size={13} color={c.onAccent} />
                      )}
                    </View>
                  </TouchableOpacity>

                  {/*
                    How far in the audition is, drawn along the row's own
                    bottom edge rather than beside the time: the bar is the
                    glance and the time is the reading, and a creator looking
                    for either finds it in the row they pressed.
                  */}
                  {isAudition && (
                    <View
                      pointerEvents="none"
                      testID={`${testID}Progress-${track.id}`}
                      style={ss.progressTrack}>
                      <View
                        testID={`${testID}ProgressFill-${track.id}`}
                        style={[ss.progressFill, {width: `${progressPct}%`}]}
                      />
                    </View>
                  )}
                </View>
              )
            })
          )}

          <View style={ss.footer}>
            <TouchableOpacity
              accessibilityRole="button"
              accessibilityLabel={l`Remove background music`}
              accessibilityHint={l`Plays no music under the pages`}
              onPress={() => onSelect(null)}
              testID={`${testID}Remove`}
              style={[
                ss.footerBtn,
                selected === null && {
                  borderColor: c.accentBorder,
                  backgroundColor: c.accentTint,
                },
              ]}>
              <Feather name="volume-x" size={14} color={c.textMuted} />
              <Text style={ss.footerTxt}>{l`No music`}</Text>
              {selected === null && (
                <Feather name="check" size={13} color={c.accent} />
              )}
            </TouchableOpacity>

            {selected !== undefined && (
              <TouchableOpacity
                accessibilityRole="button"
                accessibilityLabel={l`Use the theme's own music`}
                accessibilityHint={l`Goes back to the bed the theme plays`}
                onPress={() => onSelect(undefined)}
                testID={`${testID}Theme`}
                style={ss.footerBtn}>
                <Feather name="refresh-ccw" size={14} color={c.textMuted} />
                <Text style={ss.footerTxt}>{l`Theme default`}</Text>
              </TouchableOpacity>
            )}
            <Text style={ss.footnote}>
              {l`Music fades away while a page's own audio plays.`}
            </Text>
          </View>
        </ScrollView>
      </View>
    </>
  )
}

const makeStyles = (c: StudioChrome) =>
  StyleSheet.create({
    backdrop: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: c.scrim,
      zIndex: 90,
    },
    sheet: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      height: '88%',
      maxHeight: '88%',
      backgroundColor: c.surface,
      borderTopLeftRadius: 22,
      borderTopRightRadius: 22,
      borderTopWidth: 1,
      borderColor: c.borderStrong,
      zIndex: 100,
      overflow: 'hidden',
      shadowColor: c.shadow,
      shadowOpacity: 0.28,
      shadowRadius: 18,
      shadowOffset: {width: 0, height: -6},
      elevation: 12,
    },
    handle: {
      width: 36,
      height: 4,
      borderRadius: 2,
      alignSelf: 'center',
      marginTop: 10,
      marginBottom: 10,
      backgroundColor: c.borderStrong,
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      paddingHorizontal: 16,
      paddingBottom: 12,
      borderBottomWidth: 1,
      borderBottomColor: c.border,
    },
    headerText: {flex: 1, minWidth: 0},
    title: {color: c.text, fontSize: 17, fontWeight: '800'},
    subtitle: {color: c.textMuted, fontSize: 12, marginTop: 2},
    close: {
      width: 30,
      height: 30,
      borderRadius: 15,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: c.fill,
    },
    searchWrap: {paddingHorizontal: 14, paddingTop: 12},
    search: {
      backgroundColor: c.fill,
      borderColor: c.borderStrong,
      borderWidth: 1,
      borderRadius: 12,
      paddingHorizontal: 12,
      paddingVertical: 9,
      fontSize: 13,
      lineHeight: 18,
      color: c.text,
    },
    chipsScroll: {flexGrow: 0, marginTop: 10},
    chips: {paddingHorizontal: 14, gap: 7, alignItems: 'center'},
    chip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      paddingHorizontal: 11,
      paddingVertical: 6,
      borderRadius: 999,
      borderWidth: 1,
      borderColor: c.borderStrong,
      backgroundColor: c.fill,
    },
    chipTxt: {color: c.textMuted, fontSize: 12, fontWeight: '700'},
    chipCount: {color: c.textFaint, fontSize: 10.5, fontWeight: '800'},
    list: {flex: 1, marginTop: 12},
    listContent: {paddingHorizontal: 14, paddingBottom: 26, gap: 8},
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 11,
      paddingHorizontal: 11,
      paddingVertical: 9,
      borderRadius: 14,
      borderWidth: 1,
      borderColor: c.borderStrong,
      backgroundColor: c.fill,
    },
    audition: {
      width: 34,
      height: 34,
      borderRadius: 17,
      borderWidth: 1,
      borderColor: c.borderStrong,
      backgroundColor: c.surface,
      alignItems: 'center',
      justifyContent: 'center',
    },
    /** The play glyph's optical nudge — see where it is used. */
    playNudge: {marginLeft: 2},
    rowTap: {flex: 1, flexDirection: 'row', alignItems: 'center', gap: 11},
    rowText: {flex: 1, minWidth: 0},
    rowTitle: {color: c.text, fontSize: 13.5, fontWeight: '700'},
    rowMeta: {color: c.textMuted, fontSize: 11, marginTop: 1},
    progressTrack: {
      position: 'absolute',
      left: 12,
      right: 12,
      bottom: 2,
      height: 2,
      borderRadius: 1,
      overflow: 'hidden',
      backgroundColor: c.borderStrong,
    },
    progressFill: {height: '100%', backgroundColor: c.accent},
    mark: {
      width: 22,
      height: 22,
      borderRadius: 11,
      borderWidth: 1,
      borderColor: c.borderStrong,
      alignItems: 'center',
      justifyContent: 'center',
    },
    empty: {
      alignItems: 'center',
      gap: 8,
      paddingVertical: 34,
    },
    emptyTxt: {color: c.textMuted, fontSize: 12.5, textAlign: 'center'},
    footer: {marginTop: 8, gap: 8},
    footerBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      paddingHorizontal: 12,
      paddingVertical: 10,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: c.borderStrong,
      backgroundColor: c.fill,
    },
    footerTxt: {flex: 1, color: c.text, fontSize: 12.5, fontWeight: '700'},
    footnote: {
      color: c.textFaint,
      fontSize: 11,
      textAlign: 'center',
      paddingHorizontal: 20,
      paddingTop: 2,
    },
  })

/** The sheet's styles for the appearance the app is in. */
const useStyles = () => useStudioStyles(makeStyles)
